import type { Uuid } from "spacetimedb";
import { CheckIcon } from "lucide-react";

import { cn } from "@/web/lib/styling";
import { formatLastSoldLabel, formatSqft } from "./formatters";
import type { ReviewComp } from "./types";

/**
 * @deprecated The intro step now renders a map instead of this list. Still used
 * by `TopThreeSelectionReview` for picking from the interested-comp shortlist —
 * once that step gets its own affordance, remove this file.
 */
export function CompList({
  comps,
  selectedCompIds,
  onToggleComp,
  inset,
}: {
  comps: ReviewComp[];
  selectedCompIds?: Uuid[];
  onToggleComp?: (compId: Uuid) => void;
  inset?: boolean;
}) {
  return (
    <ul className={cn("pb-16", inset ? "space-y-3 px-2 pt-3" : "")}>
      {comps.map((comp) => {
        const selected = selectedCompIds?.some((id) => id.compareTo(comp.id) === 0) ?? false;
        return (
          <li key={comp.id.toString()}>
            <CompRow
              comp={comp}
              selected={selected}
              onClick={onToggleComp ? () => onToggleComp(comp.id) : undefined}
            />
          </li>
        );
      })}
    </ul>
  );
}

function CompRow({
  comp,
  selected,
  onClick,
}: {
  comp: ReviewComp;
  selected?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <div
      className={cn(
        "relative flex items-center justify-between gap-4 border-b border-withus-dark-blue/10 py-5",
        onClick ? "px-2" : "",
        selected
          ? "after:pointer-events-none after:absolute after:inset-0 after:border-2 after:border-withus-red after:content-['']"
          : "",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-col gap-0.5 text-sm leading-snug text-withus-dark-blue">
          <p className="font-semibold">{comp.address.addressLine1}</p>
          <p>
            {comp.address.city} {comp.address.state} {comp.address.zip}
          </p>
        </div>
        <p className="mt-2 text-sm leading-snug text-withus-dark-blue">
          Occupied by <strong>{comp.occupiedBy ?? "Unknown tenant"}</strong>
        </p>
        <p className="mt-1 text-sm leading-snug text-withus-dark-blue">
          {comp.sqft != null ? `${formatSqft(comp.sqft)} sqft · ` : ""}
          {formatLastSoldLabel(comp)}
        </p>
      </div>
      <div className="size-16 shrink-0 overflow-hidden bg-muted">
        {comp.imageUrl ? (
          <img src={comp.imageUrl} alt="" className="size-full object-cover" />
        ) : null}
      </div>
      {selected ? (
        <div className="pointer-events-none absolute -top-2 -right-2 z-10 flex size-5 items-center justify-center rounded-full bg-withus-red text-withus-white">
          <CheckIcon className="size-3 stroke-3" />
        </div>
      ) : null}
    </div>
  );

  if (onClick == null) return content;

  return (
    <button type="button" onClick={onClick} className="w-full text-left" aria-pressed={selected}>
      {content}
    </button>
  );
}
